/**
 * useNotifications Hook
 *
 * Core hook for fetching and managing notifications.
 * Provides notification data, counts, and mutation methods.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useBusinessContext } from '@/context/BusinessContext';
import type {
  Notification,
  NotificationCountByCategory,
  NotificationCountByPriority,
  UseNotificationsReturn,
} from '@/types/notifications';

// Query key factory for consistent cache keys
export const notificationQueryKeys = {
  all: ['notifications'] as const,
  list: (businessId: string | null) => [...notificationQueryKeys.all, 'list', businessId] as const,
  counts: (businessId: string | null) => [...notificationQueryKeys.all, 'counts', businessId] as const,
};

export function useNotifications(): UseNotificationsReturn {
  const { user } = useAuth();
  const { selectedBusinessId } = useBusinessContext();
  const queryClient = useQueryClient();

  // Fetch notifications for the current business/user
  const query = useQuery({
    queryKey: notificationQueryKeys.list(selectedBusinessId),
    queryFn: async (): Promise<Notification[]> => {
      if (!user) return [];

      let queryBuilder = supabase
        .from('notifications')
        .select('*')
        .is('dismissed_at', null)
        .order('created_at', { ascending: false })
        .limit(100);

      // Filter by business if one is selected
      if (selectedBusinessId) {
        queryBuilder = queryBuilder.or(`business_id.eq.${selectedBusinessId},user_id.eq.${user.id}`);
      } else {
        // No business selected - only show user-specific notifications
        queryBuilder = queryBuilder.eq('user_id', user.id);
      }

      const { data, error } = await queryBuilder;

      if (error) {
        console.error('Error fetching notifications:', error);
        throw error;
      }

      return (data || []) as Notification[];
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: true,
  });

  const notifications = query.data || [];

  // Calculate counts
  const unreadCount = notifications.filter(n => !n.read_at).length;

  const countByCategory: NotificationCountByCategory = notifications.reduce(
    (acc, notification) => {
      if (!notification.read_at) {
        acc[notification.category] = (acc[notification.category] || 0) + 1;
      }
      return acc;
    },
    {} as NotificationCountByCategory
  );

  const countByPriority: NotificationCountByPriority = notifications.reduce(
    (acc, notification) => {
      if (!notification.read_at) {
        acc[notification.priority]++;
      }
      return acc;
    },
    { urgent: 0, action_required: 0, info: 0 }
  );

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.list(selectedBusinessId),
      });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;

      let queryBuilder = supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .is('read_at', null)
        .is('dismissed_at', null);

      if (selectedBusinessId) {
        queryBuilder = queryBuilder.or(`business_id.eq.${selectedBusinessId},user_id.eq.${user.id}`);
      } else {
        queryBuilder = queryBuilder.eq('user_id', user.id);
      }

      const { error } = await queryBuilder;
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.list(selectedBusinessId),
      });
    },
  });

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    },
    onMutate: async (id) => {
      // Optimistically remove the notification from the list
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.list(selectedBusinessId),
      });

      const previousNotifications = queryClient.getQueryData<Notification[]>(
        notificationQueryKeys.list(selectedBusinessId)
      );

      queryClient.setQueryData<Notification[]>(
        notificationQueryKeys.list(selectedBusinessId),
        (old) => old?.filter(n => n.id !== id) || []
      );

      return { previousNotifications };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          notificationQueryKeys.list(selectedBusinessId),
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.list(selectedBusinessId),
      });
    },
  });

  // Dismiss all mutation
  const dismissAllMutation = useMutation({
    mutationFn: async () => {
      if (!user) return;

      let queryBuilder = supabase
        .from('notifications')
        .update({ dismissed_at: new Date().toISOString() })
        .is('dismissed_at', null);

      if (selectedBusinessId) {
        queryBuilder = queryBuilder.or(`business_id.eq.${selectedBusinessId},user_id.eq.${user.id}`);
      } else {
        queryBuilder = queryBuilder.eq('user_id', user.id);
      }

      const { error } = await queryBuilder;
      if (error) throw error;
    },
    onMutate: async () => {
      // Optimistically clear all notifications
      await queryClient.cancelQueries({
        queryKey: notificationQueryKeys.list(selectedBusinessId),
      });

      const previousNotifications = queryClient.getQueryData<Notification[]>(
        notificationQueryKeys.list(selectedBusinessId)
      );

      queryClient.setQueryData<Notification[]>(
        notificationQueryKeys.list(selectedBusinessId),
        []
      );

      return { previousNotifications };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          notificationQueryKeys.list(selectedBusinessId),
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.list(selectedBusinessId),
      });
    },
  });

  return {
    notifications,
    isLoading: query.isLoading,
    error: query.error,
    unreadCount,
    countByCategory,
    countByPriority,
    markAsRead: markAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
    dismiss: dismissMutation.mutateAsync,
    dismissAll: dismissAllMutation.mutateAsync,
    refetch: query.refetch,
  };
}
